import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, Lightbulb, FolderTree, Folder, FolderPlus } from "lucide-react";
import TaxonomyPicker from "@/components/TaxonomyPicker";
import MarkdownEditor from "@/components/MarkdownEditor";
import TagsInput from "@/components/TagsInput";
import Pager, { usePager } from "@/components/Pager";

// Flatten the folder tree into a list of { folder, depth } for non-recursive rendering.
function flattenTree(byParent, parentId, depth, out) {
  const kids = byParent[parentId] || [];
  for (let i = 0; i < kids.length; i++) {
    out.push({ folder: kids[i], depth });
    flattenTree(byParent, kids[i].id, depth + 1, out);
  }
}

const EMPTY = {
  title: "", taxonomy_id: null, folder_id: null, overview: "",
  research_gap: "", suggested_methodology: "", literature_review: "",
  relevant_papers: [],
  summary_doc_url: "", summary_doc_base64: "",
  video_url: "",
  tags: [], is_premium: true, difficulty: "intermediate",
  estimated_duration_months: null,
};

export default function AdminResearchIdeasPage() {
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null); // null = all, "ROOT" = unfiled, "<id>" = folder
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showFolderUI, setShowFolderUI] = useState(false);
  const [folderForm, setFolderForm] = useState({ name: "", parent_id: null, color: "#0b3d91", description: "" });
  const [editingFolder, setEditingFolder] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, fr] = await Promise.all([
        api.get("/admin/research-ideas"),
        api.get("/research-folders"),
      ]);
      setItems(r.data || []);
      setFolders(fr.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Build folder tree by parent + flat list with depth for iterative rendering
  const flatFolders = useMemo(() => {
    const byParent = {};
    folders.forEach((f) => {
      const p = f.parent_id || "ROOT";
      if (!byParent[p]) byParent[p] = [];
      byParent[p].push(f);
    });
    const out = [];
    flattenTree(byParent, "ROOT", 0, out);
    return out;
  }, [folders]);

  const onEditFolder = (f) => {
    setEditingFolder(f);
    setFolderForm({ name: f.name, parent_id: f.parent_id, color: f.color, description: f.description });
    setShowFolderUI(true);
  };

  const saveFolder = async () => {
    if (!folderForm.name.trim()) { toast.error("Folder name required"); return; }
    try {
      if (editingFolder) {
        await api.put(`/admin/research-folders/${editingFolder.id}`, folderForm);
        toast.success("Folder updated");
      } else {
        await api.post("/admin/research-folders", folderForm);
        toast.success("Folder created");
      }
      setShowFolderUI(false); setEditingFolder(null); setFolderForm({ name: "", parent_id: null, color: "#0b3d91", description: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteFolder = async (id) => {
    const cascade = window.confirm("Delete folder. Click OK to also delete subfolders & detach ideas, Cancel to only delete this empty folder.");
    try {
      await api.delete(`/admin/research-folders/${id}`, { params: { cascade } });
      toast.success("Folder deleted");
      setShowFolderUI(false); setEditingFolder(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const moveIdea = async (ideaId, folderId) => {
    try {
      await api.post(`/admin/research-ideas/${ideaId}/move`, { folder_id: folderId || null });
      toast.success("Moved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Move failed"); }
  };

  const startNew = () => { setEditing("new"); setForm({ ...EMPTY, folder_id: activeFolder && activeFolder !== "ROOT" ? activeFolder : null }); };

  // Filtered ideas for the active folder view
  const filteredIdeas = useMemo(() => {
    if (activeFolder === null) return items;
    if (activeFolder === "ROOT") return items.filter((i) => !i.folder_id);
    return items.filter((i) => i.folder_id === activeFolder);
  }, [items, activeFolder]);
  const ideasPager = usePager(filteredIdeas, 16);
  const startEdit = (i) => {
    setEditing(i.id);
    setForm({ ...EMPTY, ...i, tags: i.tags || [], relevant_papers: i.relevant_papers || [] });
  };
  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try {
      if (editing === "new") await api.post("/admin/research-ideas", form);
      else await api.put(`/admin/research-ideas/${editing}`, form);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this research idea? This cannot be undone.")) return;
    await api.delete(`/admin/research-ideas/${id}`);
    toast.success("Deleted"); load();
  };

  const addPaper = () => setForm({ ...form, relevant_papers: [...(form.relevant_papers || []), { title: "", authors: "", year: "", doi: "", url: "" }] });
  const updPaper = (i, k, v) => setForm({ ...form, relevant_papers: form.relevant_papers.map((p, idx) => idx === i ? { ...p, [k]: v } : p) });
  const delPaper = (i) => setForm({ ...form, relevant_papers: form.relevant_papers.filter((_, idx) => idx !== i) });

  return (
    <div data-testid="admin-research-ideas-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">RESEARCH IDEA CENTER</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Curate <em className="italic text-kb-primary">research ideas</em></h1>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">Add, edit, and organise premium research ideas with literature reviews, methodology, and summary briefs.</p>
        </div>
        <button onClick={startNew} className="kb-btn-primary text-sm" data-testid="idea-new-btn"><Plus className="w-3.5 h-3.5" /> New idea</button>
      </div>

      {editing && (
        <div className="mt-6 kb-card p-6" data-testid="idea-editor">
          <div className="flex items-center justify-between mb-4">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary">{editing === "new" ? "NEW IDEA" : "EDITING"}</div>
            <button onClick={() => setEditing(null)} className="text-kb-ink-muted"><X className="w-4 h-4" /></button>
          </div>
          <div className="space-y-4">
            <input className="kb-input text-sm w-full" placeholder="Title *"
              value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="idea-title-input" />
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Domain hierarchy</label>
              <TaxonomyPicker domain="research_ideas" value={form.taxonomy_id} onChange={(id) => setForm({ ...form, taxonomy_id: id })} />
            </div>
            <textarea rows={2} className="kb-input text-sm w-full resize-none" placeholder="Overview (short teaser)"
              value={form.overview} onChange={(e) => setForm({ ...form, overview: e.target.value })} />
            <div className="grid sm:grid-cols-3 gap-2">
              <select className="kb-input text-sm" value={form.difficulty}
                onChange={(e) => setForm({ ...form, difficulty: e.target.value })}>
                {["beginner", "intermediate", "advanced"].map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <input type="number" min="0" className="kb-input text-sm" placeholder="Duration (months)"
                value={form.estimated_duration_months ?? ""}
                onChange={(e) => setForm({ ...form, estimated_duration_months: e.target.value ? parseInt(e.target.value) : null })} />
              <label className="flex items-center gap-2 text-sm text-kb-ink-muted">
                <input type="checkbox" checked={!!form.is_premium}
                  onChange={(e) => setForm({ ...form, is_premium: e.target.checked })} />
                Premium gated
              </label>
            </div>
            <TagsInput
              value={form.tags || []}
              onChange={(tags) => setForm({ ...form, tags })}
              placeholder="Tags (type and press Enter or comma)"
              testId="idea-tags"
            />
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Research gap *</label>
              <MarkdownEditor value={form.research_gap} onChange={(v) => setForm({ ...form, research_gap: v })} />
            </div>
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Suggested methodology</label>
              <MarkdownEditor value={form.suggested_methodology} onChange={(v) => setForm({ ...form, suggested_methodology: v })} />
            </div>
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Literature review</label>
              <MarkdownEditor value={form.literature_review} onChange={(v) => setForm({ ...form, literature_review: v })} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Relevant papers</label>
                <button onClick={addPaper} className="kb-btn-outline text-xs" data-testid="idea-add-paper"><Plus className="w-3 h-3" /> Add</button>
              </div>
              <div className="space-y-2">
                {(form.relevant_papers || []).map((p, i) => (
                  <div key={i} className="border border-slate-200 rounded-sm p-3 grid grid-cols-2 gap-2 relative">
                    <input className="kb-input text-xs col-span-2" placeholder="Title" value={p.title}
                      onChange={(e) => updPaper(i, "title", e.target.value)} />
                    <input className="kb-input text-xs" placeholder="Authors" value={p.authors}
                      onChange={(e) => updPaper(i, "authors", e.target.value)} />
                    <input className="kb-input text-xs" placeholder="Year" value={p.year}
                      onChange={(e) => updPaper(i, "year", e.target.value)} />
                    <input className="kb-input text-xs" placeholder="DOI" value={p.doi}
                      onChange={(e) => updPaper(i, "doi", e.target.value)} />
                    <input className="kb-input text-xs" placeholder="URL" value={p.url}
                      onChange={(e) => updPaper(i, "url", e.target.value)} />
                    <button onClick={() => delPaper(i)} className="absolute top-1 right-1 text-rose-600"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
            <input className="kb-input text-sm w-full" placeholder="Summary doc URL (PDF link, optional)"
              value={form.summary_doc_url || ""} onChange={(e) => setForm({ ...form, summary_doc_url: e.target.value })} />
            <input className="kb-input text-sm w-full" placeholder="Explainer video URL (YouTube/Vimeo/MP4, optional)"
              value={form.video_url || ""} onChange={(e) => setForm({ ...form, video_url: e.target.value })}
              data-testid="idea-video-url-input" />
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Folder (optional)</label>
              <select className="kb-input text-sm w-full mt-1" value={form.folder_id || ""} onChange={(e) => setForm({ ...form, folder_id: e.target.value || null })} data-testid="idea-folder-select">
                <option value="">— Unfiled —</option>
                {folders.map((f) => <option key={f.id} value={f.id}>{f.path || f.name}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={save} className="kb-btn-primary text-sm" data-testid="idea-save-btn">Save idea</button>
            <button onClick={() => setEditing(null)} className="kb-btn-outline text-sm">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        <div className="mt-8 grid lg:grid-cols-[260px_1fr] gap-6">
          {/* Folder tree sidebar */}
          <aside className="bg-white border border-slate-200 rounded-sm p-3 h-fit lg:sticky lg:top-4" data-testid="admin-research-folder-tree">
            <div className="flex items-center justify-between mb-2">
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted flex items-center gap-1.5"><FolderTree className="w-3.5 h-3.5" /> Folders</div>
              <button onClick={() => { setEditingFolder(null); setFolderForm({ name: "", parent_id: activeFolder && activeFolder !== "ROOT" ? activeFolder : null, color: "#0b3d91", description: "" }); setShowFolderUI(true); }} className="text-kb-primary hover:bg-kb-primary/5 rounded p-1" title="New folder" data-testid="admin-folder-new-btn">
                <FolderPlus className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="space-y-0.5 group">
              <button onClick={() => setActiveFolder(null)} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm text-left hover:bg-slate-100 ${activeFolder === null ? "bg-blue-50 text-kb-primary font-semibold" : "text-kb-ink"}`} data-testid="admin-folder-all">
                <Lightbulb className="w-3.5 h-3.5" /> All ideas <span className="ml-auto text-[10px] text-kb-ink-light">{items.length}</span>
              </button>
              <button onClick={() => setActiveFolder("ROOT")} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm text-left hover:bg-slate-100 ${activeFolder === "ROOT" ? "bg-blue-50 text-kb-primary font-semibold" : "text-kb-ink"}`} data-testid="admin-folder-unfiled">
                <Folder className="w-3.5 h-3.5" /> Unfiled <span className="ml-auto text-[10px] text-kb-ink-light">{items.filter((i) => !i.folder_id).length}</span>
              </button>
              <div className="pt-2 border-t border-slate-100 mt-2">
                {flatFolders.map(({ folder: f, depth }) => (
                  <button
                    key={f.id}
                    onClick={() => setActiveFolder(f.id)}
                    className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-sm text-sm text-left hover:bg-slate-100 ${activeFolder === f.id ? "bg-blue-50 text-kb-primary font-semibold" : "text-kb-ink"}`}
                    style={{ paddingLeft: `${8 + depth * 14}px` }}
                    data-testid={`admin-folder-${f.id}`}
                  >
                    <span className="flex items-center gap-1.5 truncate">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: f.color }} />
                      <span className="truncate">{f.name}</span>
                    </span>
                    <span onClick={(e) => { e.stopPropagation(); onEditFolder(f); }} className="text-kb-ink-light hover:text-kb-primary px-1" title="Edit">
                      <Edit3 className="w-3 h-3" />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Ideas grid (filtered by activeFolder) */}
          <div>
            {filteredIdeas.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">
                No ideas in this {activeFolder ? "folder" : "catalogue"} yet. Click "New idea" to add one.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {ideasPager.pageItems.map((i) => (
                  <div key={i.id} className="kb-card p-5" data-testid={`admin-idea-${i.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <Lightbulb className="w-5 h-5 text-kb-primary" strokeWidth={1.5} />
                        <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-2 line-clamp-1">
                          {(i.path || []).join(" › ") || "—"}
                        </div>
                        <h3 className="font-serif text-lg text-kb-ink mt-1 leading-snug line-clamp-2">{i.title}</h3>
                        <div className="kb-mono text-[10px] uppercase tracking-wider mt-3 text-kb-ink-light">
                          {i.is_premium ? "PREMIUM" : "FREE"} · {i.difficulty?.toUpperCase()} · {(i.relevant_papers || []).length} REFS
                          {i.video_url && " · VIDEO"}
                        </div>
                        <div className="mt-2">
                          <select
                            value={i.folder_id || ""}
                            onChange={(e) => moveIdea(i.id, e.target.value)}
                            className="text-[11px] border border-slate-200 rounded px-2 py-1 bg-white text-kb-ink-muted"
                            data-testid={`admin-idea-${i.id}-move`}
                          >
                            <option value="">— Unfiled —</option>
                            {folders.map((f) => <option key={f.id} value={f.id}>{f.path || f.name}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <button onClick={() => startEdit(i)} className="p-1.5 text-kb-ink-muted hover:text-kb-primary" data-testid={`admin-idea-${i.id}-edit`}><Edit3 className="w-3.5 h-3.5" /></button>
                        <button onClick={() => remove(i.id)} className="p-1.5 text-rose-600 hover:text-rose-700" data-testid={`admin-idea-${i.id}-delete`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Pager {...ideasPager} label="ideas" testId="admin-research-ideas-pager" />
          </div>
        </div>}

      {/* Folder editor modal */}
      {showFolderUI && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="admin-folder-modal">
          <div className="bg-white rounded-sm shadow-xl w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-lg text-kb-ink">{editingFolder ? "Edit folder" : "New folder"}</h3>
              <button onClick={() => { setShowFolderUI(false); setEditingFolder(null); }} className="text-kb-ink-muted"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <input className="kb-input text-sm w-full" placeholder="Folder name *" value={folderForm.name} onChange={(e) => setFolderForm({ ...folderForm, name: e.target.value })} autoFocus data-testid="folder-name-input" />
              <select className="kb-input text-sm w-full" value={folderForm.parent_id || ""} onChange={(e) => setFolderForm({ ...folderForm, parent_id: e.target.value || null })} data-testid="folder-parent-select">
                <option value="">— Root level —</option>
                {folders.filter((f) => f.id !== editingFolder?.id).map((f) => <option key={f.id} value={f.id}>{f.path || f.name}</option>)}
              </select>
              <div className="flex items-center gap-3">
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Color</label>
                <input type="color" value={folderForm.color} onChange={(e) => setFolderForm({ ...folderForm, color: e.target.value })} className="w-10 h-8 rounded cursor-pointer" />
              </div>
              <textarea rows={2} className="kb-input text-sm w-full resize-none" placeholder="Description (optional)" value={folderForm.description} onChange={(e) => setFolderForm({ ...folderForm, description: e.target.value })} />
            </div>
            <div className="mt-4 flex items-center justify-between gap-2">
              {editingFolder && (
                <button onClick={() => deleteFolder(editingFolder.id)} className="text-rose-600 text-sm flex items-center gap-1 hover:underline" data-testid="folder-delete-btn">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              )}
              <div className="ml-auto flex gap-2">
                <button onClick={() => { setShowFolderUI(false); setEditingFolder(null); }} className="kb-btn-outline text-sm">Cancel</button>
                <button onClick={saveFolder} className="kb-btn-primary text-sm" data-testid="folder-save-btn">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
